import { useCallback, useState, useMemo, useEffect, useRef } from "react";
import { createViewServiceProviders } from "@/modules/lasalle/partners/views/ViewServiceProviders";
import { NOOP } from "@/utils/utils";
import { fetchAllCoaMappings } from "@/useCases/coaMappings";
import type { TDealCountsSummary } from "@/modules/lasalle/partners/views/ViewServiceProviders";
import type { TAsyncState } from "@/store";
import type {
    TApiColumnMappingFileType,
    TApiColumnMappingFieldType,
    TApiDealCountsByBusinessPartner,
} from "@/apis/proda_finance";
import type { TCompositionDeps } from "@/ui/compositionRoot/types";
import { NO_SEGMENTS } from "./noSegments";
import { COA_MAPPING_MODE } from "./fileFormats";

// Add business_partner_type to SpQuery so the Type filter flows server-side.
type TSpQuery = {
    skip: number;
    limit: number;
    name?: string;
    business_partner_ids?: string[];
    employee_ids?: string[];
    employees_unassigned?: boolean;
    business_partner_type?: string;
    can_upload_supporting_files?: boolean;
    is_active?: boolean;
    sort_by?: string;
    sort_dir?: string;
};

type TSpItem = {
    id: string;
    companyId: string;
    name: string;
    code: string;
    businessPartnerType: "service_provider" | "portfolio_company";
    contactEmail: string | null;
    contactName: string | null;
    contactPhone: string | null;
    defaultDateFormat: string;
    defaultPeriodFormat: string;
    currencyId: string | null;
    canUploadSupportingFiles: boolean;
    isActive: boolean;
    isTest: boolean;
    employees: Array<{ id: string; display_name: string; email: string }>;
    notes: string | null;
    createdAt: string;
    updatedAt: string;
};

type TCombinedState = {
    status: "idle" | "pending" | "fulfilled" | "rejected";
    data: {
        items: TSpItem[];
        total: number;
        totalUnfiltered: number;
        page: number;
        pageSize: number;
    } | null;
    error: Error | null;
};

/**
 * Pair the counts endpoint's two maps into per-business-partner totals.
 *
 * `counts` drives the result: it holds every business partner that has any deal,
 * and a partner absent from it has no deals at all, so it stays absent here and
 * the badge hides.
 *
 * If `active_counts` is missing altogether the response predates it (a stale API
 * during a rolling deploy), and every partner is reported as having **no** counts
 * rather than zero active deals: an absent badge is honest, whereas "0/5" on a
 * business partner whose deals are all active is a believable lie.
 *
 * Exported for testing; the hook that calls it needs the whole view rendered.
 */
export function pairDealCounts(
    response: TApiDealCountsByBusinessPartner,
): Record<string, TDealCountsSummary> {
    if (!response.active_counts) return {};
    return Object.fromEntries(
        Object.entries(response.counts).map(([bpId, total]) => [
            bpId,
            { total, active: response.active_counts[bpId] ?? 0 },
        ]),
    );
}

export const wireBusinessPartnersList = ({ store: _store, useAppStore, api }: TCompositionDeps) => {
    const ViewServiceProviders = createViewServiceProviders({
        useCases: () => {
            const effectiveCompanyId = useAppStore((state) => state.effectiveCompanyId);
            const currentUser = useAppStore((state) => state.user);
            const canCreate = currentUser?.permissions.includes("business_partner_create") ?? false;
            const canCreateDeal = currentUser?.permissions.includes("deal_create") ?? false;
            // The "Deals" button opens a business partner's deals *list*, which only
            // needs deal_view (held by every subgroup, incl. Business Partners). The
            // backend scopes that list to the viewer's own deals; opening a single
            // deal still requires deal_overview_view, enforced on the detail route.
            const canViewDeals = currentUser?.permissions.includes("deal_view") ?? false;
            const [spState, setSpState] = useState<{
                status: "idle" | "pending" | "fulfilled" | "rejected";
                items: TSpItem[];
                total: number;
                totalUnfiltered: number;
                page: number;
                pageSize: number;
                error: Error | null;
            }>({
                status: "idle",
                items: [],
                total: 0,
                totalUnfiltered: 0,
                page: 1,
                pageSize: 50,
                error: null,
            });

            const [pcItems, setPcItems] = useState<TSpItem[]>([]);
            const [dealCounts, setDealCounts] = useState<Record<string, TDealCountsSummary>>({});
            const lastSpQueryRef = useRef<TSpQuery>({ skip: 0, limit: 50 });

            // Unified BP loader — calls /v1/m/lasalle/partners/business-partners (SP+PC in one response).
            // spState now holds the unified BP page (not just SPs); name kept to minimise churn.
            const loadServiceProviders = useCallback(
                (query: TSpQuery, signal?: AbortSignal) => {
                    lastSpQueryRef.current = query;
                    setSpState((prev) => ({ ...prev, status: "pending" }));
                    api.businessPartners
                        .list(query, signal)
                        .then((bpResponse) => {
                            const pageSize = query.limit;
                            const skip = query.skip;
                            const page = Math.floor(skip / pageSize) + 1;
                            const items = bpResponse.items.map((sp) => ({
                                id: sp.id,
                                companyId: sp.company_id,
                                name: sp.name,
                                code: sp.code,
                                businessPartnerType: sp.business_partner_type,
                                contactEmail: sp.contact_email,
                                contactName: sp.contact_name,
                                contactPhone: sp.contact_phone,
                                // PC rows return null for SP-only fields; fall back to display defaults.
                                defaultDateFormat: sp.default_date_format ?? "YYYY-MM-DD",
                                defaultPeriodFormat: sp.default_period_format ?? "YYYY-MM",
                                currencyId: sp.currency_id,
                                canUploadSupportingFiles: sp.can_upload_supporting_files,
                                isActive: sp.is_active,
                                isTest: sp.is_test,
                                employees: (sp.employees ?? []).map((e) => ({
                                    id: e.id,
                                    display_name: e.display_name,
                                    email: e.email,
                                })),
                                notes: sp.notes,
                                createdAt: sp.created_at,
                                updatedAt: sp.updated_at,
                            }));
                            setSpState({
                                status: "fulfilled",
                                items,
                                total: bpResponse.total,
                                totalUnfiltered: bpResponse.total_unfiltered ?? bpResponse.total,
                                page,
                                pageSize,
                                error: null,
                            });
                        })
                        .catch((error: unknown) => {
                            // Swallow abort errors — the superseding request owns the loading state.
                            if (error instanceof DOMException && error.name === "AbortError") {
                                return;
                            }
                            console.error("Failed to load business partners:", error);
                            const message =
                                error instanceof Error
                                    ? error.message
                                    : "Failed to load business partners";
                            setSpState((prev) => ({
                                ...prev,
                                status: "rejected",
                                error: new Error(message),
                            }));
                        });
                },
                // eslint-disable-next-line react-hooks/exhaustive-deps
                [effectiveCompanyId],
            );

            // Dedicated reference state for deal-creation dropdowns.
            // spSelectorItems: full SP list (not limited to the current list page).
            const [spSelectorItems, setSpSelectorItems] = useState<
                Array<{ id: string; name: string; code: string }>
            >([]);
            const [spSelectorLoading, setSpSelectorLoading] = useState(true);

            // Options for the Name and Employees multiSelect filters — the full
            // server-scoped sets, not just what the current page happens to show.
            const [businessPartnerOptions, setBusinessPartnerOptions] = useState<
                Array<{ value: string; label: string }>
            >([]);
            const [employeeOptions, setEmployeeOptions] = useState<
                Array<{ value: string; label: string }>
            >([]);

            // Reference data loader — deal counts + full SP reference + PC dropdown list
            // + Name filter options.
            // pcItems feeds only the PC dropdown (never concatenated into the list table).
            const loadReferenceData = useCallback(async () => {
                setSpSelectorLoading(true);
                try {
                    const [spRefResponse, pcResponse, dealCountsResponse, filterOptionsResponse] =
                        await Promise.all([
                            api.serviceProviders.list({ skip: 0, limit: 1000 }),
                            api.portfolioCompanies.list({ skip: 0, limit: 1000 }),
                            api.deals.countsByBusinessPartner(),
                            api.businessPartners.filterOptions(),
                        ]);
                    setDealCounts(pairDealCounts(dealCountsResponse));
                    setBusinessPartnerOptions(
                        filterOptionsResponse.businessPartners.map((bp) => ({
                            value: bp.id,
                            label: bp.name,
                        })),
                    );
                    setEmployeeOptions(
                        filterOptionsResponse.employees.map((employee) => ({
                            value: employee.id,
                            label: employee.name,
                        })),
                    );
                    setSpSelectorItems(
                        spRefResponse.items.map((sp) => ({
                            id: sp.id,
                            name: sp.name,
                            code: sp.code,
                        })),
                    );
                    setPcItems(
                        pcResponse.items.map((pc) => ({
                            id: pc.id,
                            companyId: pc.company_id,
                            name: pc.name,
                            code: pc.code,
                            businessPartnerType: "portfolio_company" as const,
                            contactEmail: null,
                            contactName: null,
                            contactPhone: null,
                            defaultDateFormat: "YYYY-MM-DD",
                            defaultPeriodFormat: "YYYY-MM",
                            currencyId: null,
                            canUploadSupportingFiles: pc.can_upload_supporting_files ?? false,
                            isActive: pc.is_active,
                            isTest: pc.is_test ?? false,
                            employees:
                                pc.employees?.map((e) => ({
                                    id: e.id,
                                    display_name: e.display_name,
                                    email: e.email,
                                })) ?? [],
                            notes: pc.notes,
                            createdAt: pc.created_at,
                            updatedAt: pc.updated_at,
                        })),
                    );
                } catch (error) {
                    console.error("Failed to load reference data / deal counts:", error);
                } finally {
                    setSpSelectorLoading(false);
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
            }, [effectiveCompanyId]);

            // Combined view state — now just the unified BP page (no PC concat).
            const combinedState = useMemo<TCombinedState>(() => {
                if (spState.status === "idle") {
                    return { status: "idle", data: null, error: null };
                }
                if (spState.status === "rejected") {
                    return { status: "rejected", data: null, error: spState.error };
                }
                return {
                    status: spState.status,
                    data: {
                        items: spState.items,
                        total: spState.total,
                        totalUnfiltered: spState.totalUnfiltered,
                        page: spState.page,
                        pageSize: spState.pageSize,
                    },
                    error: null,
                };
            }, [spState]);

            const onCreateServiceProvider = useCallback(
                async (data: {
                    name: string;
                    businessPartnerType?: "service_provider" | "portfolio_company";
                    contactEmail?: string | null;
                    contactName?: string | null;
                    contactPhone?: string | null;
                    defaultDateFormat?: string;
                    defaultPeriodFormat?: string;
                    currencyId?: string | null;
                    canUploadSupportingFiles?: boolean;
                    isTest?: boolean;
                    employeeIds?: string[];
                }) => {
                    if (data.businessPartnerType === "portfolio_company") {
                        const created = await api.portfolioCompanies.create({
                            name: data.name,
                            coa_mapping_mode: COA_MAPPING_MODE,
                            employee_ids: data.employeeIds,
                        } as any);
                        // Refresh unified list + reference data after PC create.
                        loadServiceProviders(lastSpQueryRef.current);
                        await loadReferenceData();
                        return {
                            id: created.id,
                            companyId: created.company_id,
                            name: created.name,
                            code: created.code,
                            businessPartnerType: "portfolio_company" as const,
                            contactEmail: null,
                            contactName: null,
                            contactPhone: null,
                            defaultDateFormat: "YYYY-MM-DD",
                            defaultPeriodFormat: "YYYY-MM",
                            currencyId: data.currencyId || null,
                            canUploadSupportingFiles: created.can_upload_supporting_files ?? true,
                            isActive: created.is_active,
                            isTest: (created as { is_test?: boolean }).is_test ?? false,
                            employees: (created as any).employees || [],
                            notes: created.notes,
                            createdAt: created.created_at,
                            updatedAt: created.updated_at,
                        };
                    }

                    const created = await api.serviceProviders.create({
                        name: data.name,
                        business_partner_type: "service_provider",
                        contact_email: data.contactEmail,
                        contact_name: data.contactName,
                        contact_phone: data.contactPhone,
                        default_date_format: data.defaultDateFormat,
                        default_period_format: data.defaultPeriodFormat,
                        currency_id: data.currencyId,
                        can_upload_supporting_files: data.canUploadSupportingFiles,
                        coa_mapping_mode: COA_MAPPING_MODE,
                        is_test: data.isTest,
                        employee_ids: data.employeeIds,
                    } as any);
                    // Refresh unified list + SP reference after SP create.
                    loadServiceProviders(lastSpQueryRef.current);
                    void loadReferenceData();
                    return {
                        id: created.id,
                        companyId: created.company_id,
                        name: created.name,
                        code: created.code,
                        businessPartnerType: created.business_partner_type,
                        contactEmail: created.contact_email,
                        contactName: created.contact_name,
                        contactPhone: created.contact_phone,
                        defaultDateFormat: created.default_date_format,
                        defaultPeriodFormat: created.default_period_format,
                        currencyId: created.currency_id,
                        canUploadSupportingFiles: created.can_upload_supporting_files,
                        isActive: created.is_active,
                        isTest: created.is_test,
                        employees: (created as any).employees || [],
                        notes: created.notes,
                        createdAt: created.created_at,
                        updatedAt: created.updated_at,
                    };
                },
                [loadServiceProviders, loadReferenceData],
            );

            const onUpdateServiceProvider = useCallback(
                async (
                    id: string,
                    data: {
                        name?: string;
                        contactEmail?: string | null;
                        contactName?: string | null;
                        contactPhone?: string | null;
                        defaultDateFormat?: string;
                        defaultPeriodFormat?: string;
                        canUploadSupportingFiles?: boolean;
                        isActive?: boolean;
                        isTest?: boolean;
                        notes?: string | null;
                        employeeIds?: string[];
                    },
                    businessPartnerType?: "service_provider" | "portfolio_company",
                ) => {
                    const isPC = businessPartnerType === "portfolio_company";

                    if (
                        data.name !== undefined ||
                        data.canUploadSupportingFiles !== undefined ||
                        data.isTest !== undefined ||
                        data.notes !== undefined ||
                        data.contactEmail !== undefined ||
                        data.contactName !== undefined ||
                        data.contactPhone !== undefined ||
                        data.defaultDateFormat !== undefined ||
                        data.defaultPeriodFormat !== undefined
                    ) {
                        if (isPC) {
                            await api.portfolioCompanies.update(id, {
                                name: data.name,
                                can_upload_supporting_files: data.canUploadSupportingFiles,
                                is_test: data.isTest,
                                notes: data.notes,
                            });
                        } else {
                            await api.serviceProviders.update(id, {
                                name: data.name,
                                contact_email: data.contactEmail,
                                contact_name: data.contactName,
                                contact_phone: data.contactPhone,
                                default_date_format: data.defaultDateFormat,
                                default_period_format: data.defaultPeriodFormat,
                                can_upload_supporting_files: data.canUploadSupportingFiles,
                                is_test: data.isTest,
                                notes: data.notes,
                            });
                        }
                    }

                    if (data.isActive !== undefined) {
                        if (isPC) {
                            if (data.isActive) {
                                await api.portfolioCompanies.activate(id);
                            } else {
                                await api.portfolioCompanies.deactivate(id);
                            }
                        } else {
                            if (data.isActive) {
                                await api.serviceProviders.activate(id);
                            } else {
                                await api.serviceProviders.deactivate(id);
                            }
                        }
                    }

                    if (data.employeeIds !== undefined) {
                        if (isPC) {
                            await api.portfolioCompanies.setEmployees(id, data.employeeIds);
                        } else {
                            await api.serviceProviders.setEmployees(id, data.employeeIds);
                        }
                    }

                    // After any update, refresh unified list; also refresh reference data
                    // when a PC changed (so the PC dropdown stays current).
                    loadServiceProviders(lastSpQueryRef.current);
                    if (isPC) {
                        await loadReferenceData();
                    } else {
                        void loadReferenceData();
                    }
                },
                [loadServiceProviders, loadReferenceData],
            );

            const onDeleteServiceProvider = useCallback(
                async (
                    id: string,
                    businessPartnerType?: "service_provider" | "portfolio_company",
                ) => {
                    if (businessPartnerType === "portfolio_company") {
                        await api.portfolioCompanies.delete(id);
                        // Refresh unified list + reference data after PC delete.
                        loadServiceProviders(lastSpQueryRef.current);
                        await loadReferenceData();
                    } else {
                        await api.serviceProviders.delete(id);
                        // Refresh unified list + SP reference after SP delete.
                        loadServiceProviders(lastSpQueryRef.current);
                        void loadReferenceData();
                    }
                },
                [loadServiceProviders, loadReferenceData],
            );

            const onCreateBusinessPartnerWithCoa = useCallback(
                async (
                    data: {
                        name: string;
                        businessPartnerType?: "service_provider" | "portfolio_company";
                        contactEmail?: string | null;
                        contactName?: string | null;
                        contactPhone?: string | null;
                        defaultDateFormat?: string;
                        defaultPeriodFormat?: string;
                        currencyId?: string | null;
                        canUploadSupportingFiles?: boolean;
                        isTest?: boolean;
                        employeeIds?: string[];
                    },
                    file: File,
                    businessPartnerType: "service_provider" | "portfolio_company",
                ) => {
                    if (businessPartnerType === "portfolio_company") {
                        const created = await api.portfolioCompanies.createWithCoa(
                            {
                                name: data.name,
                                coa_mapping_mode: COA_MAPPING_MODE,
                                employee_ids: data.employeeIds,
                            } as any,
                            file,
                        );
                        // Refresh unified list + reference data after PC create-with-coa.
                        loadServiceProviders(lastSpQueryRef.current);
                        await loadReferenceData();
                        return {
                            id: created.id,
                            companyId: created.company_id,
                            name: created.name,
                            code: created.code,
                            businessPartnerType: "portfolio_company" as const,
                            contactEmail: null,
                            contactName: null,
                            contactPhone: null,
                            defaultDateFormat: "YYYY-MM-DD",
                            defaultPeriodFormat: "YYYY-MM",
                            currencyId: data.currencyId || null,
                            canUploadSupportingFiles: created.can_upload_supporting_files ?? true,
                            isActive: created.is_active,
                            isTest: (created as { is_test?: boolean }).is_test ?? false,
                            employees: (created as any).employees || [],
                            notes: created.notes,
                            createdAt: created.created_at,
                            updatedAt: created.updated_at,
                        };
                    }

                    const created = await api.serviceProviders.createWithCoa(
                        {
                            name: data.name,
                            business_partner_type: "service_provider",
                            contact_email: data.contactEmail,
                            contact_name: data.contactName,
                            contact_phone: data.contactPhone,
                            default_date_format: data.defaultDateFormat,
                            default_period_format: data.defaultPeriodFormat,
                            currency_id: data.currencyId,
                            can_upload_supporting_files: data.canUploadSupportingFiles,
                            coa_mapping_mode: COA_MAPPING_MODE,
                            is_test: data.isTest,
                            employee_ids: data.employeeIds,
                        } as any,
                        file,
                    );
                    // Refresh unified list + SP reference after SP create-with-coa.
                    loadServiceProviders(lastSpQueryRef.current);
                    void loadReferenceData();
                    return {
                        id: created.id,
                        companyId: created.company_id,
                        name: created.name,
                        code: created.code,
                        businessPartnerType: created.business_partner_type,
                        contactEmail: created.contact_email,
                        contactName: created.contact_name,
                        contactPhone: created.contact_phone,
                        defaultDateFormat: created.default_date_format,
                        defaultPeriodFormat: created.default_period_format,
                        currencyId: created.currency_id,
                        canUploadSupportingFiles: created.can_upload_supporting_files,
                        isActive: created.is_active,
                        isTest: created.is_test,
                        employees: (created as any).employees || [],
                        notes: created.notes,
                        createdAt: created.created_at,
                        updatedAt: created.updated_at,
                    };
                },
                [loadServiceProviders, loadReferenceData],
            );

            const onUploadCoA = useCallback(
                async (
                    ownerId: string,
                    file: File,
                    businessPartnerType: "service_provider" | "portfolio_company",
                ) => {
                    const result =
                        businessPartnerType === "portfolio_company"
                            ? await api.portfolioCompanies.uploadCoA(ownerId, file)
                            : await api.serviceProviders.uploadCoA(ownerId, file);
                    return {
                        success: result.success,
                        errors: result.errors,
                        warnings: result.warnings,
                        mappingsCreated: result.mappings_created,
                        mappingsDeleted: result.mappings_deleted,
                        storagePath: result.storage_path,
                    };
                },
                [],
            );

            const onLoadCoAMappings = useCallback(
                async (
                    ownerId: string,
                    businessPartnerType: "service_provider" | "portfolio_company",
                ) => {
                    // Every mapping, not the first page: this feeds the
                    // post-upload summary, whose "mappings created" count is the
                    // length of what is returned here.
                    const items = await fetchAllCoaMappings(
                        businessPartnerType === "portfolio_company"
                            ? api.portfolioCompanies
                            : api.serviceProviders,
                        ownerId,
                    );
                    return items.map((m) => ({
                        id: m.id,
                        thirdAccountCode: m.third_account_code,
                        thirdAccountName: m.third_account_name,
                        yardiAccountId: m.yardi_account_id,
                        yardiAccountCode: m.yardi_account_code,
                        yardiAccountName: m.yardi_account_name,
                        isInForce: m.is_in_force,
                        isActive: m.is_active,
                        createdAt: m.created_at,
                        updatedAt: m.updated_at,
                    }));
                },
                [],
            );

            const onLoadDeals = useCallback(
                async (
                    bpId: string,
                    businessPartnerType: "service_provider" | "portfolio_company",
                ) => {
                    const response =
                        businessPartnerType === "service_provider"
                            ? await api.deals.list({
                                  service_provider_id: bpId,
                                  limit: 100,
                              })
                            : await api.deals.list({
                                  portfolio_company_id: bpId,
                                  limit: 100,
                              });
                    return response.items.map((d) => ({
                        id: d.id,
                        companyId: d.company_id,
                        name: d.name,
                        code: d.code,
                        serviceProviderId: d.service_provider_id,
                        portfolioCompanyId: d.portfolio_company_id,
                        financeUserIds: d.finance_user_ids ?? [],
                        dealFileType: d.deal_file_type,
                        glType: d.gl_type,
                        importAvailable: d.import_available,
                        finalFileDestination: d.final_file_destination,
                        ...NO_SEGMENTS,
                        monthlyDeadlineDay: d.monthly_deadline_day,
                        quarterlyDeadlineDay: d.quarterly_deadline_day,
                        yearEndDeadline: d.year_end_deadline,
                        startPeriod: d.start_period,
                        yardiEntityId: d.yardi_entity_id,
                        yardiDatabase: d.yardi_database,
                        acceptsOtb: d.accepts_otb,
                        otbComparisonEnabled: d.otb_comparison_enabled,
                        acceptsFinJournals: d.accepts_fin_journals,
                        isActive: d.is_active,
                        isTest: d.is_test,
                        notes: d.notes,
                        tbFilteringRules: [],
                        glFilteringRules: [],
                        activatedAt: d.activated_at,
                        deactivatedAt: d.deactivated_at,
                        createdAt: d.created_at,
                        updatedAt: d.updated_at,
                    }));
                },
                [],
            );

            const onLoadDealPropcodeMappings = useCallback(async (dealId: string) => {
                const response = await api.deals.getPropcodeMappings(dealId);
                return response.items.map((m) => ({
                    id: m.id,
                    sourcePropertyCode: m.source_property_code,
                    sourcePropertyName: m.source_property_name,
                    targetPropertyCode: m.target_property_code,
                    targetPropertyName: m.target_property_name,
                    isActive: m.is_active,
                }));
            }, []);

            // SP selector now sourced from dedicated reference fetch (not the list page).
            // This ensures the full SP set is always available in deal-creation dropdowns.
            const serviceProvidersForDealsState = useMemo<{
                data: Array<{ id: string; name: string; code: string }> | null;
                loading: boolean;
            }>(() => {
                return { data: spSelectorItems, loading: spSelectorLoading };
            }, [spSelectorItems, spSelectorLoading]);

            const portfolioCompaniesState = useMemo<{
                data: Array<{ id: string; name: string; code: string }> | null;
                loading: boolean;
            }>(() => {
                const items = pcItems.map((pc) => ({ id: pc.id, name: pc.name, code: pc.code }));
                return { data: items, loading: false };
            }, [pcItems]);

            const [usersState, setUsersState] = useState<{
                data: Array<{ id: string; displayName: string; email: string }> | null;
                loading: boolean;
            }>({ data: null, loading: true });

            useEffect(() => {
                const loadUsers = async () => {
                    try {
                        const response = await api.users.list({
                            page_size: 100,
                            business_partners: true,
                        });
                        setUsersState({
                            data: response.items.map((u) => ({
                                id: u.id,
                                displayName: u.display_name,
                                email: u.email,
                            })),
                            loading: false,
                        });
                    } catch {
                        setUsersState({ data: [], loading: false });
                    }
                };

                loadUsers();
            }, []);

            const onCreateDeal = useCallback(
                async (data: {
                    name: string;
                    code: string;
                    serviceProviderId: string;
                    portfolioCompanyId: string;
                    financeUserIds: string[];
                    dealFileType: string;
                    glType: string;
                    finalFileDestination: string;
                    monthlyDeadlineDay: number;
                    startPeriod: string;
                    propcodeMappings: Array<{
                        level: string;
                        source_propcode: string;
                        target_propcode: string;
                    }>;
                    columnMappings: Array<{
                        file_type: TApiColumnMappingFileType;
                        field_type: TApiColumnMappingFieldType;
                        column_name: string;
                        header_row_number: number;
                    }>;
                    acceptsOtb?: boolean;
                }) => {
                    const createdDeal = await api.deals.create({
                        name: data.name,
                        code: data.code,
                        service_provider_id: data.serviceProviderId,
                        portfolio_company_id: data.portfolioCompanyId,
                        finance_user_ids: data.financeUserIds,
                        deal_file_type: data.dealFileType as "tb_and_gl" | "tb_only",
                        final_file_destination: data.finalFileDestination as
                            | "yardi"
                            | "data_platform",
                        monthly_deadline_day: data.monthlyDeadlineDay,
                        start_period: data.startPeriod,
                        propcode_mappings: data.propcodeMappings,
                        column_mappings: data.columnMappings,
                        accepts_otb: data.acceptsOtb,
                    });
                    return createdDeal;
                },
                [],
            );

            const onUploadOTB = useCallback(async (dealId: string, file: File) => {
                return api.deals.uploadOTB(dealId, file);
            }, []);

            const onValidatePropcodeMappingFile = useCallback(async (file: File) => {
                return await api.deals.validatePropcodeMappingFile(file);
            }, []);

            return {
                // onMount triggers the reference data loader (SP selector + PC dropdown + deal counts).
                onMount: loadReferenceData,
                onUnmount: NOOP,
                useServiceProvidersState: () =>
                    combinedState as TAsyncState<(typeof combinedState)["data"]>,
                onCreateServiceProvider,
                onCreateBusinessPartnerWithCoa,
                onUpdateServiceProvider,
                onDeleteServiceProvider,
                onRefresh: () => loadServiceProviders(lastSpQueryRef.current),
                onUploadCoA,
                onLoadCoAMappings,
                useDealCounts: () => dealCounts,
                onLoadDeals,
                onLoadDealPropcodeMappings,
                useServiceProvidersForDeals: () => serviceProvidersForDealsState,
                usePortfolioCompanies: () => portfolioCompaniesState,
                useUsers: () => usersState,
                onCreateDeal,
                onValidatePropcodeMappingFile,
                onUploadOTB,
                onDeleteDeal: useCallback(async (dealId: string) => {
                    await api.deals.delete(dealId);
                }, []),
                onExtractColumns: async (file: File, headerRowNumber: number) => {
                    return api.deals.extractColumns(file, headerRowNumber);
                },
                loadServiceProviders,
                useTotalUnfiltered: () => spState.totalUnfiltered,
                useBusinessPartnerOptions: () => businessPartnerOptions,
                useEmployeeOptions: () => employeeOptions,
                canCreate,
                canCreateDeal,
                canViewDeals,
            };
        },
    });

    return { ViewServiceProviders };
};
